import { LaptopMinimal, LoaderCircle, Smartphone } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { SheetOrModal } from "~/components/Modal";
import { faDate } from "~/design/account/format";
import Button from "~/design/ui/Button";
import to from "~/utils/to";

const renderPlatformIcon = ({ session }) => {
  if (!session.device?.version) return <LaptopMinimal className="size-7" strokeWidth={1.75} />;
  if (session.device?.version === "mobile") return <Smartphone className="size-7" strokeWidth={1.75} />;
};

function RevokeConfirmation({ visible, onClose, session, onSubmit }: { visible: boolean; onClose: () => void; session: any; onSubmit?: () => Promise<unknown> }) {
  const [submitting, setSubmitting] = React.useState(false);
  const { t } = useTranslation();

  const Content = () => {
    const details = [
      { label: t("ui.profile.revoke.os"), value: [session.os?.family, session.os?.version].filter(Boolean).join(" "), ltr: true },
      { label: t("ui.profile.revoke.browser"), value: session.browser?.family, ltr: true },
      { label: t("ui.profile.revoke.country"), value: session?.country, ltr: true },
      { label: t("ui.profile.revoke.ip"), value: session?.ip, ltr: true },
      { label: t("ui.profile.revoke.lastSeen"), value: session?.updatedAt && faDate(session.updatedAt) },
    ].filter((row) => row.value);

    return (
      <div className="flex flex-col">
        <div className="flex flex-col items-center text-center">
          <span className="grid size-16 place-items-center rounded-full bg-danger-soft text-danger">{renderPlatformIcon({ session })}</span>
          <h2 className="mt-4 font-display text-xl font-bold leading-relaxed text-ink">{t("ui.profile.revoke.title")}</h2>
          <p className="mt-1 max-w-[34ch] text-sm leading-7 text-ink-2">{t("ui.profile.revoke.body")}</p>
        </div>

        <dl className="mt-5 divide-y divide-line rounded-md border border-line bg-bg-2 px-4">
          {details.map((row) => (
            <div key={row.label} className="flex min-h-12 items-center justify-between gap-4 py-2 text-sm">
              <dt className="shrink-0 text-ink-3">{row.label}</dt>
              <dd className={row.ltr ? "ltr-iso truncate font-medium text-ink" : "font-medium text-ink"}>{row.value}</dd>
            </div>
          ))}
        </dl>

        <div className="mt-6 flex flex-col gap-2">
          <Button
            variant="danger"
            size="lg"
            block
            onClick={async () => {
              setSubmitting(true);
              const [] = await to(onSubmit());
              onClose();
              setSubmitting(false);
            }}
            disabled={submitting}
            aria-busy={submitting}
          >
            {submitting && <LoaderCircle className="size-5 animate-spin" strokeWidth={1.75} />}
            {submitting ? t("ui.profile.revoke.wait") : t("ui.profile.revoke.confirm")}
          </Button>
          <Button variant="ghost" size="lg" block onClick={onClose} disabled={submitting}>
            {t("ui.profile.revoke.cancel")}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <SheetOrModal visible={visible} onClose={onClose} label={t("ui.profile.revoke.title")}>
      <Content />
    </SheetOrModal>
  );
}

export default RevokeConfirmation;
